import { retryConnection } from '@/api/hooks/useIntegrationLogs/mutations'
import { LinkButton } from '@/components/Display/LinkButton'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { friendlySagaName } from '@/constants/sagaTypes'
import {
  BulkRetryConfirm,
  bulkRetryLabel,
} from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/BulkRetryButton'
import { issueCategoryLabel } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/categories'
import { formatRelativeTime, formatShortDateTime } from '@/utils/dateFormatters'
import { ArrowRightOutlined } from '@ant-design/icons'
import {
  BBDTag,
  Horizontal,
  Texto,
  Vertical,
  useThemeContext,
} from '@gravitate-js/excalibrr'
import { Button, Card } from 'antd'
import { type MouseEvent, useState } from 'react'
import {
  issueOwner,
  issueTone,
  issueToneColor,
  orderLogsPath,
  otherTenantName,
} from './issueFields'
import { type IssueGroup, bulkRetryLogGroupIds } from './issueGroups'

/** How many order numbers a card spells out before it just says how many more. */
const ORDERS_SHOWN = 3

/** "Order #1", or "3 orders: #1, #2, #3", or "5 orders: #1, #2, #3 +2". */
function ordersText(numbers: string[]): string | null {
  if (numbers.length === 0) return null
  if (numbers.length === 1) return `Order #${numbers[0]}`
  const shown = numbers.slice(0, ORDERS_SHOWN).map((n) => `#${n}`)
  const more = numbers.length - shown.length
  return `${numbers.length} orders: ${shown.join(', ')}${more ? ` +${more}` : ''}`
}

const retryStyle = {
  background: 'var(--foreground)',
  borderColor: 'var(--foreground)',
  color: 'var(--background)',
}

interface IssueCardProps {
  /** One unique issue and every open failure on it in the window. */
  group: IssueGroup
  /** Whether this is the issue open in the details panel. */
  selected: boolean
  tenantId: string | null
  tenantName: string | null
  onViewIssue: (group: IssueGroup) => void
  onRetried: () => void
}

export function IssueCard({
  group,
  selected,
  tenantId,
  tenantName,
  onViewIssue,
  onRetried,
}: IssueCardProps) {
  const { isDarkTheme } = useThemeContext()
  const [retrying, setRetrying] = useState(false)

  // The latest failure speaks for the issue; they all share a key, so the
  // category and message only differ in what changed since.
  const issue = group.latest
  const tone = issueTone(issue)
  const { orderNumbers } = group
  // Retry and the order link act on one order. An issue across several is
  // bulk retried instead, so they only show on a single one.
  // Retrying a lone order retries its root failure, not a skip it caused.
  const singleOrder = group.transactions === 1
  const orderNumber = singleOrder ? (orderNumbers[0] ?? null) : null
  const groupId = issue.log_group_id
  const sagaName = friendlySagaName(issue.saga_type)
  const partners = [
    ...new Set(group.logs.map((log) => otherTenantName(log, tenantId))),
  ]

  // Retry replays the saga behind the log, which needs both the log and the
  // group it came from; a log without either has nothing to replay.
  const canRetry = !!tenantName && !!groupId && !issue.retried
  const bulkIds = singleOrder ? [] : bulkRetryLogGroupIds(group)

  const handleRetry = async () => {
    if (!tenantName || !groupId) return
    setRetrying(true)
    try {
      await retryConnection(tenantName, issue, groupId)
      onRetried()
    } finally {
      setRetrying(false)
    }
  }

  const viewIssue = () => onViewIssue(group)

  // Each button does its own thing, so none of them should also count as a
  // click on the card behind it.
  const stopping = (action: () => void) => (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    action()
  }

  return (
    <Card
      size='small'
      hoverable
      role='button'
      tabIndex={0}
      onClick={viewIssue}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          viewIssue()
        }
      }}
      style={{
        // Outlined like the selected count card above, with the severity
        // stripe left intact. Transparent rather than absent when unselected
        // so picking a card does not shift the list by two pixels.
        border: `2px solid ${selected ? 'hsl(var(--primary))' : 'transparent'}`,
        borderLeft: `4px solid ${issueToneColor(tone, isDarkTheme)}`,
      }}
      styles={{ body: { padding: 12 } }}
    >
      <Vertical gap={8}>
        <Horizontal justifyContent='space-between' alignItems='center' gap={8}>
          <Horizontal gap={6} verticalCenter style={{ flexWrap: 'wrap' }}>
            {issue.status === 'warning' ? (
              <BBDTag warning>Warning</BBDTag>
            ) : (
              <SeverityTag severity={issue.severity} />
            )}
            <BBDTag>
              {issueOwner(issue, tenantId) === 'mine' ? 'Mine' : 'Partner'}
            </BBDTag>
          </Horizontal>
          <Horizontal gap={6} verticalCenter>
            {singleOrder && (
              <Button
                size='small'
                loading={retrying}
                disabled={!canRetry}
                onClick={stopping(handleRetry)}
                style={retryStyle}
              >
                Retry
              </Button>
            )}
            {!singleOrder && (
              <span
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <BulkRetryConfirm
                  logGroupIds={bulkIds}
                  description={`Crossroads will retry failed events on the ${bulkIds.length === 1 ? 'order' : `${bulkIds.length} orders`} this issue is holding up.`}
                  emptyTooltip='Nothing left to retry on this issue'
                  onStarted={onRetried}
                  renderButton={({ disabled, loading }) => (
                    <Button
                      size='small'
                      loading={loading}
                      disabled={disabled}
                      style={retryStyle}
                    >
                      {bulkRetryLabel(bulkIds.length)}
                    </Button>
                  )}
                />
              </span>
            )}
            <Button size='small' type='primary' onClick={stopping(viewIssue)}>
              View Issue
            </Button>
            {singleOrder && (
              <LinkButton
                size='small'
                disabled={!orderNumber}
                to={orderNumber ? orderLogsPath(orderNumber) : ''}
                onClick={(event) => event.stopPropagation()}
              >
                View Order Logs <ArrowRightOutlined style={{ fontSize: 10 }} />
              </LinkButton>
            )}
          </Horizontal>
        </Horizontal>

        <Vertical gap={2}>
          <Horizontal gap={6} verticalCenter style={{ flexWrap: 'wrap' }}>
            <Texto weight={600}>{issueCategoryLabel(issue) ?? 'Issue'}</Texto>
            {/* Plain BBDTag, the same gray chip the log details panel
                gives a saga name (see V3LogDetailsPanel). */}
            {sagaName && <BBDTag>{sagaName}</BBDTag>}
          </Horizontal>
          {/* The log's own title only restates the category and the saga
              beside it; the detail message is the part that says what
              actually went wrong. */}
          <Texto>{issue.detail.message}</Texto>
        </Vertical>

        <Horizontal justifyContent='space-between' alignItems='center' gap={8}>
          <Texto appearance='hint' style={{ fontSize: 12 }}>
            {[
              ordersText(orderNumbers),
              `${partners.length > 1 ? 'Partners' : 'Partner'}: ${partners.join(', ')}`,
            ]
              .filter(Boolean)
              .join('  •  ')}
          </Texto>
          <Texto appearance='hint' style={{ fontSize: 12 }}>
            {`${formatRelativeTime(issue.date)}  •  ${formatShortDateTime(issue.date)}`}
          </Texto>
        </Horizontal>
      </Vertical>
    </Card>
  )
}
